import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Film, Camera, Aperture, Move, Sun, Loader2, Download,
  Sparkles, Play, Clock, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CinemaStudioPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [presets, setPresets] = useState(null);
  const [scene, setScene] = useState('');
  const [cameraBody, setCameraBody] = useState('arri_alexa');
  const [lens, setLens] = useState('prime_50');
  const [aperture, setAperture] = useState('f/2.8');
  const [motion, setMotion] = useState('dolly_in');
  const [speed, setSpeed] = useState('normal');
  const [lighting, setLighting] = useState('natural');
  const [aspectRatio, setAspectRatio] = useState('21:9');
  const [duration, setDuration] = useState(4);

  const [generating, setGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [builtPrompt, setBuiltPrompt] = useState('');

  useEffect(() => {
    axios.get(`${API}/cinema/presets`).then(r => setPresets(r.data)).catch(() => {});
  }, []);

  const pollJob = async (jobId) => {
    for (let i = 0; i < 120; i++) {
      await new Promise(r => setTimeout(r, 4000));
      try {
        const { data } = await axios.get(`${API}/cinema/jobs/${jobId}`);
        setJobStatus(data.status);
        if (data.status === 'completed') return data.result;
        if (data.status === 'failed') throw new Error(data.error || 'Generation failed');
      } catch (err) {
        if (err.message?.includes('failed') || err.message?.includes('404')) throw err;
      }
    }
    throw new Error('Timed out');
  };

  const handleGenerate = async () => {
    if (!scene.trim()) return toast.error('Describe your scene first');
    setGenerating(true);
    setResult(null);
    setJobStatus('starting');
    try {
      const { data } = await axios.post(`${API}/cinema/generate`, {
        scene_description: scene,
        camera_body: cameraBody,
        lens,
        aperture,
        motion,
        speed,
        lighting,
        aspect_ratio: aspectRatio,
        duration,
      }, { headers });
      setBuiltPrompt(data.prompt);
      toast.info('Cinematic shot queued — rendering...');
      const res = await pollJob(data.job_id);
      setResult(res);
      toast.success('Shot rendered!');
    } catch (err) {
      toast.error(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  if (!presets) {
    return (
      <AppShell>
        <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="cinema-page">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Film className="w-5 h-5 text-orange-400" />
                <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="cinema-title">Cinema Studio</h1>
              </div>
              <p className="text-sm text-white/40">Professional camera controls for cinematic AI video</p>
            </div>
            <Button onClick={handleGenerate} disabled={generating} data-testid="cinema-generate-btn"
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Rendering</> : <><Sparkles className="w-4 h-4 mr-2" />Render Shot</>}
            </Button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: controls */}
          <div className="lg:col-span-2 space-y-6">
            {/* Scene */}
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              <Label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Scene Description</Label>
              <Textarea value={scene} onChange={e => setScene(e.target.value)} data-testid="cinema-scene"
                placeholder="A lone astronaut walks across an alien desert at dusk, two moons rising on the horizon..."
                className="bg-black/40 border-white/[0.06] text-white min-h-[100px] resize-none" />
            </section>

            {/* Camera Body */}
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              <Label className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-2"><Camera className="w-3 h-3" />Camera Body</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="cinema-bodies">
                {presets.camera_bodies.map(b => (
                  <button key={b.id} onClick={() => setCameraBody(b.id)} data-testid={`body-${b.id}`}
                    className={`text-left p-3 rounded-lg border transition-all ${cameraBody === b.id ? 'border-orange-500/60 bg-orange-500/10' : 'border-white/[0.06] bg-black/20 hover:border-white/20'}`}>
                    <div className="text-[12px] font-semibold text-white/90">{b.name}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">{b.sensor}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Lens */}
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              <Label className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-2"><Aperture className="w-3 h-3" />Lens</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="cinema-lenses">
                {presets.lenses.map(l => (
                  <button key={l.id} onClick={() => setLens(l.id)} data-testid={`lens-${l.id}`}
                    className={`text-left p-3 rounded-lg border transition-all ${lens === l.id ? 'border-orange-500/60 bg-orange-500/10' : 'border-white/[0.06] bg-black/20 hover:border-white/20'}`}>
                    <div className="text-[12px] font-semibold text-white/90">{l.name}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">{l.type}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Motion */}
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              <Label className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-2"><Move className="w-3 h-3" />Camera Motion</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="cinema-motions">
                {presets.motion_types.map(m => (
                  <button key={m.id} onClick={() => setMotion(m.id)} data-testid={`motion-${m.id}`}
                    className={`text-left p-3 rounded-lg border transition-all ${motion === m.id ? 'border-orange-500/60 bg-orange-500/10' : 'border-white/[0.06] bg-black/20 hover:border-white/20'}`}>
                    <div className="text-[12px] font-semibold text-white/90">{m.name}</div>
                    <div className="text-[10px] text-white/40 mt-0.5 line-clamp-1">{m.desc}</div>
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* Right: parameters + preview */}
          <div className="space-y-6">
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5 space-y-4">
              <h3 className="text-xs uppercase tracking-wider text-white/40 mb-1">Parameters</h3>

              <div>
                <Label className="text-[11px] text-white/60 mb-1.5 block">Aperture</Label>
                <select value={aperture} onChange={e => setAperture(e.target.value)} data-testid="cinema-aperture"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white">
                  {presets.apertures.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-[11px] text-white/60 mb-1.5 block">Speed</Label>
                <div className="grid grid-cols-3 gap-1" data-testid="cinema-speed">
                  {presets.speeds.map(s => (
                    <button key={s} onClick={() => setSpeed(s)} data-testid={`speed-${s}`}
                      className={`py-1.5 rounded-md text-xs capitalize transition-all ${speed === s ? 'bg-orange-500/20 text-orange-300' : 'bg-black/30 text-white/50 hover:text-white/80'}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[11px] text-white/60 mb-1.5 block flex items-center gap-1"><Sun className="w-3 h-3" />Lighting</Label>
                <select value={lighting} onChange={e => setLighting(e.target.value)} data-testid="cinema-lighting"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white">
                  {presets.lighting_presets.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-[11px] text-white/60 mb-1.5 block">Aspect Ratio</Label>
                <div className="grid grid-cols-3 gap-1" data-testid="cinema-aspect">
                  {presets.aspect_ratios.map(a => (
                    <button key={a} onClick={() => setAspectRatio(a)} data-testid={`aspect-${a.replace(':','x')}`}
                      className={`py-1.5 rounded-md text-xs transition-all ${aspectRatio === a ? 'bg-orange-500/20 text-orange-300' : 'bg-black/30 text-white/50 hover:text-white/80'}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[11px] text-white/60 mb-1.5 block flex items-center gap-1"><Clock className="w-3 h-3" />Duration: {duration}s</Label>
                <input type="range" min={2} max={10} value={duration} onChange={e => setDuration(Number(e.target.value))} data-testid="cinema-duration"
                  className="w-full accent-orange-500" />
              </div>
            </section>

            {/* Preview */}
            <section className="rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-5">
              <h3 className="text-xs uppercase tracking-wider text-white/40 mb-3">Preview</h3>
              {generating && (
                <div className="aspect-video bg-black/60 rounded-lg flex flex-col items-center justify-center gap-3" data-testid="cinema-rendering">
                  <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
                  <span className="text-xs text-white/50">{jobStatus || 'starting'}...</span>
                </div>
              )}
              {!generating && result?.video_url && (
                <div className="space-y-2" data-testid="cinema-result">
                  <video src={result.video_url} controls className="w-full rounded-lg" data-testid="cinema-video" />
                  <Button onClick={() => { const a = document.createElement('a'); a.href = result.video_url; a.download = 'cinema_shot.mp4'; a.target = '_blank'; a.click(); }}
                    data-testid="cinema-download" variant="outline" className="w-full border-white/10 text-white/80 hover:bg-white/5">
                    <Download className="w-3.5 h-3.5 mr-2" />Download
                  </Button>
                </div>
              )}
              {!generating && !result && (
                <div className="aspect-video bg-black/30 rounded-lg flex items-center justify-center text-white/30 text-xs" data-testid="cinema-placeholder">
                  <Play className="w-5 h-5 mr-2" />Awaiting render
                </div>
              )}
              {builtPrompt && (
                <div className="mt-3 p-3 bg-black/40 rounded-md border border-white/[0.04]" data-testid="cinema-prompt-preview">
                  <div className="text-[10px] uppercase tracking-wider text-white/30 mb-1 flex items-center gap-1"><Wand2 className="w-2.5 h-2.5" />Compiled prompt</div>
                  <p className="text-[11px] text-white/60 leading-relaxed line-clamp-6">{builtPrompt}</p>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
